"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import JsonLd from "@/components/JsonLd";
import { generatePropertyFAQ } from "@/lib/propertyFaq";
import { isExecutiveCondo } from "@/lib/v2/propertyKind";
import { propertyFaqOverride } from "@/lib/propertyFaqOverrides";
import { formatPropertyName } from "@/lib/utils";
import { faqPageSchema } from "@/lib/schemas";
import type { ProjectProperty } from "./types";

const SALES_ENTRY = /showflat|vip|viewing|discount|price list/i;

export function ProjectFAQ({
  property,
  displayName,
  soldOut,
}: {
  property: ProjectProperty;
  displayName?: string;
  soldOut?: boolean;
}) {
  const [active, setActive] = useState<number | null>(null);
  // displayName replaces both name and title, which can drop the "EC" token
  // the kind check reads (a record named "... Ave 12 EC" displayed as
  // "Clovelle of Woodlands"). Decide the kind from the original record and
  // pass it through, so renaming for display never changes the answers.
  const source = displayName
    ? { ...property, title: displayName, name: displayName, isExecutiveCondo: isExecutiveCondo(property) }
    : { ...property, isExecutiveCondo: isExecutiveCondo(property) };
  // A hand-written set replaces the generated one outright, for the page and
  // for the schema. See lib/propertyFaqOverrides.
  const written = propertyFaqOverride(formatPropertyName(property.name));
  const faqs = (written ?? generatePropertyFAQ(source)).filter((faq) => {
    const text = `${faq.question} ${faq.answer}`;
    if (/nric/i.test(text)) return false;
    if (soldOut && SALES_ENTRY.test(faq.question)) return false;
    return true;
  });
  if (faqs.length === 0) return null;

  return (
    <>
      <JsonLd data={faqPageSchema(faqs)} />
      <div className="space-y-3">
        {faqs.map((faq, index) => {
          const open = active === index;
          return (
            <div key={faq.question} className="overflow-hidden rounded-2xl border border-line bg-white">
              <button
                type="button"
                onClick={() => setActive(open ? null : index)}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-4 p-5 text-left font-medium text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
              >
                <span>{faq.question}</span>
                <ChevronDown aria-hidden="true" className={`size-5 shrink-0 text-ink-muted transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              <div className={`grid transition-[grid-template-rows,opacity] duration-200 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                <div className="overflow-hidden">
                  <div
                    className="px-5 pb-5 text-sm leading-6 text-ink-muted [&_a]:text-brand-600 [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_p+p]:mt-3 [&_ul]:list-disc [&_ul]:pl-5"
                    dangerouslySetInnerHTML={{ __html: faq.answer }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
